const MIN_WASM_THREAD_COUNT = 1;
const MAX_WASM_THREAD_COUNT = 8;
const MIN_MEMORY_BUDGET_MIB = 64;
const MAX_MEMORY_BUDGET_MIB = 2048;
const RUNTIME_PREWARM_QUERY_PARAM = "fishy_runtime_prewarm";

export function parseBooleanParam(value) {
  if (value == null) {
    return undefined;
  }
  const normalized = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(normalized)) {
    return true;
  }
  if (["0", "false", "no", "off"].includes(normalized)) {
    return false;
  }
  return undefined;
}

export function parseIntegerParam(value) {
  if (value == null || value === "") {
    return undefined;
  }
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeDeviceMemoryGiB(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function normalizeDevicePixelRatio(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export function normalizeMemoryBudgetMiB(value) {
  const parsed = parseIntegerParam(value);
  if (parsed === undefined) {
    return undefined;
  }
  return Math.min(MAX_MEMORY_BUDGET_MIB, Math.max(MIN_MEMORY_BUDGET_MIB, parsed));
}

function firstRuntimeConfigValue(keys) {
  for (const key of keys) {
    const value = String(globalThis.window?.__fishystuffRuntimeConfig?.[key] ?? "").trim();
    if (value) {
      return value;
    }
  }
  return "";
}

function firstRuntimeQueryValue(locationLike, keys) {
  try {
    const params = new URL(locationLike?.href || "", "https://fishystuff.fish").searchParams;
    for (const key of keys) {
      if (params.has(key)) {
        return params.get(key);
      }
    }
  } catch (_) {}
  return undefined;
}

function resolveRuntimeSetting({
  explicitValue,
  locationLike,
  configKeys,
  queryKeys,
  normalize,
}) {
  const explicit = normalize(explicitValue);
  if (explicit !== undefined) {
    return explicit;
  }
  const configured = normalize(firstRuntimeConfigValue(configKeys));
  if (configured !== undefined) {
    return configured;
  }
  return normalize(firstRuntimeQueryValue(locationLike, queryKeys));
}

export function detectWasmThreadingReadiness(globalLike = globalThis) {
  const hardwareConcurrency = Number(globalLike.navigator?.hardwareConcurrency || 0);
  const deviceMemory = normalizeDeviceMemoryGiB(globalLike.navigator?.deviceMemory);
  return {
    crossOriginIsolated: globalLike.crossOriginIsolated === true,
    sharedArrayBufferAvailable: typeof globalLike.SharedArrayBuffer === "function",
    secureContext: globalLike.isSecureContext === true,
    hardwareConcurrency: Number.isFinite(hardwareConcurrency) && hardwareConcurrency > 0
      ? hardwareConcurrency
      : 0,
    deviceMemory,
  };
}

function wasmThreadingReady(readiness) {
  return Boolean(
    readiness?.crossOriginIsolated &&
      readiness?.sharedArrayBufferAvailable &&
      readiness?.secureContext,
  );
}

export function assertWasmThreadingReady(readiness) {
  if (wasmThreadingReady(readiness)) {
    return;
  }
  const missing = [];
  if (!readiness?.crossOriginIsolated) {
    missing.push("crossOriginIsolated");
  }
  if (!readiness?.sharedArrayBufferAvailable) {
    missing.push("SharedArrayBuffer");
  }
  if (!readiness?.secureContext) {
    missing.push("secure context");
  }
  throw new Error(
    `threaded map runtime unavailable: missing ${missing.join(", ") || "browser readiness"}`,
  );
}

export function resolveWasmThreadWorkerCount({
  locationLike = globalThis.location,
  explicitValue,
  hardwareConcurrency = globalThis.navigator?.hardwareConcurrency,
  deviceMemory = globalThis.navigator?.deviceMemory,
} = {}) {
  const override = resolveRuntimeSetting({
    explicitValue,
    locationLike,
    configKeys: ["wasmThreadCount", "mapWasmThreadCount"],
    queryKeys: ["wasmThreadCount"],
    normalize: parseIntegerParam,
  });
  if (override !== undefined) {
    return Math.min(MAX_WASM_THREAD_COUNT, Math.max(MIN_WASM_THREAD_COUNT, override));
  }
  const hardware = Number(hardwareConcurrency || 0);
  const deviceMemoryGiB = normalizeDeviceMemoryGiB(deviceMemory);
  if (!Number.isFinite(hardware) || hardware <= 0) {
    return 2;
  }
  let workers = Math.min(Math.max(2, Math.floor(hardware) - 1), 6);
  if (deviceMemoryGiB > 0 && deviceMemoryGiB <= 2) {
    workers = Math.min(workers, 2);
  } else if (deviceMemoryGiB > 0 && deviceMemoryGiB <= 4) {
    workers = Math.min(workers, 4);
  }
  return workers;
}

export function resolveMapRuntimeMemoryBudgetMiB({
  locationLike = globalThis.location,
  explicitValue,
  deviceMemory = globalThis.navigator?.deviceMemory,
} = {}) {
  const override = resolveRuntimeSetting({
    explicitValue,
    locationLike,
    configKeys: ["memoryBudgetMiB"],
    queryKeys: ["memoryBudgetMiB"],
    normalize: normalizeMemoryBudgetMiB,
  });
  if (override !== undefined) {
    return override;
  }
  const deviceMemoryGiB = normalizeDeviceMemoryGiB(deviceMemory);
  if (deviceMemoryGiB > 0 && deviceMemoryGiB <= 2) {
    return 256;
  }
  if (deviceMemoryGiB > 0 && deviceMemoryGiB <= 4) {
    return 512;
  }
  if (deviceMemoryGiB >= 8) {
    return 1024;
  }
  return 768;
}

export function mapRuntimePrewarmEnabled({
  locationLike = globalThis.location,
  explicitValue,
} = {}) {
  return resolveRuntimeSetting({
    explicitValue,
    locationLike,
    configKeys: ["mapRuntimePrewarm", "mapRuntimeCachePrewarm"],
    queryKeys: [RUNTIME_PREWARM_QUERY_PARAM],
    normalize: parseBooleanParam,
  }) === true;
}

function canvasPixelSize(canvas) {
  const width = Number(canvas?.width);
  const height = Number(canvas?.height);
  return {
    width: Number.isFinite(width) && width > 0 ? Math.round(width) : 0,
    height: Number.isFinite(height) && height > 0 ? Math.round(height) : 0,
  };
}

export function collectMapRuntimeResourceDiscovery({
  globalLike = globalThis,
  locationLike = globalThis.location,
  canvas = null,
  manifestInfo = null,
  wasmThreadCount = 0,
  memoryBudgetMiB,
} = {}) {
  const readiness = detectWasmThreadingReadiness(globalLike);
  const canvasSize = canvasPixelSize(canvas);
  const configuredMemoryBudgetMiB =
    normalizeMemoryBudgetMiB(memoryBudgetMiB) ??
    resolveMapRuntimeMemoryBudgetMiB({
      locationLike,
      deviceMemory: readiness.deviceMemory,
    });
  return {
    crossOriginIsolated: readiness.crossOriginIsolated,
    sharedArrayBufferAvailable: readiness.sharedArrayBufferAvailable,
    secureContext: readiness.secureContext,
    hardwareConcurrency: readiness.hardwareConcurrency,
    deviceMemory: readiness.deviceMemory,
    devicePixelRatio: normalizeDevicePixelRatio(
      globalLike.devicePixelRatio ?? globalLike.window?.devicePixelRatio,
    ),
    canvasWidth: canvasSize.width,
    canvasHeight: canvasSize.height,
    wasmThreadCount: Math.max(0, Number.parseInt(wasmThreadCount, 10) || 0),
    memoryBudgetMiB: configuredMemoryBudgetMiB,
    runtimeManifestVersion:
      Number(manifestInfo?.manifest?.schemaVersion ?? manifestInfo?.manifest?.schema_version ?? 0) ||
      0,
    runtimeManifestThreaded: manifestInfo?.threaded === true,
  };
}
